import { useState } from "react";
import { toast } from "sonner";
import { useGetCarrierServices } from "@/api/carrier-services";
import {
  useExternalCarrierAreas,
  useSaveExternalCarrierArea,
} from "@/api/external-carrier-coverage";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useGetCarrierPortalTeam,
  useUpdateCarrierPortalMembership,
} from "@/api/partner-carriers";

export function ExternalCarrierCoverageSection({
  carrierId,
}: {
  carrierId: string;
}) {
  const areas = useExternalCarrierAreas(carrierId);
  const services = useGetCarrierServices({ carrierId, active: true });
  const save = useSaveExternalCarrierArea(carrierId);
  const team = useGetCarrierPortalTeam(carrierId);
  const disableLegacyAccess = useUpdateCarrierPortalMembership();
  const [areaId, setAreaId] = useState("");
  const [draftIds, setDraftIds] = useState<string[] | null>(null);
  const selectedArea = areas.data?.find((area) => area.id === areaId);
  const currentIds =
    selectedArea?.assignment?.serviceCoverages
      .filter((coverage) => coverage.active)
      .map((coverage) => coverage.carrierServiceId) ?? [];
  const selectedIds = draftIds ?? currentIds;
  const activeServices =
    services.data?.filter((service) => service.isActive) ?? [];
  const changed =
    selectedIds.length !== currentIds.length ||
    selectedIds.some((id) => !currentIds.includes(id));
  const enabledAreas = areas.data?.filter(
    (area) =>
      area.assignment?.active &&
      area.assignment.serviceCoverages.some((coverage) => coverage.active)
  );
  const legacyMemberships =
    team.data?.members?.flatMap((member) =>
      member.memberships
        .filter(
          (membership) =>
            membership.carrierId === carrierId && membership.status === "ACTIVE"
        )
        .map((membership) => ({
          id: membership.id,
          name: member.name,
        }))
    ) ?? [];

  return (
    <section aria-label="Shipping coverage" className="space-y-3 border-t pt-4">
      <div>
        <h3 className="text-sm font-semibold">Shipping coverage</h3>
        <p className="text-xs text-muted-foreground">
          Choose the areas where each shipping service can be quoted. A service
          needs coverage at both the sender and recipient areas.
        </p>
      </div>
      {areas.isLoading || services.isLoading ? (
        <p role="status">Loading coverage…</p>
      ) : areas.error || services.error ? (
        <p role="alert" className="text-sm text-destructive">
          Coverage could not be loaded. Try reopening the carrier editor.
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {enabledAreas?.length
              ? `Enabled areas: ${enabledAreas.map((area) => area.name).join(", ")}`
              : "No services are offered in any area yet."}
          </p>
          <label
            htmlFor="external-service-area"
            className="block space-y-1 text-sm"
          >
            <span className="font-medium">Service area</span>
            <select
              id="external-service-area"
              value={areaId}
              disabled={save.isPending}
              onChange={(event) => {
                setAreaId(event.target.value);
                setDraftIds(null);
              }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3"
            >
              <option value="">Select an area…</option>
              {areas.data?.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>
          </label>
          {selectedArea && (
            <div className="space-y-3 rounded-md border p-3">
              <p className="text-sm font-medium">
                Services offered in {selectedArea.name}
              </p>
              {activeServices.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Add a shipping service before configuring coverage.
                </p>
              )}
              {activeServices.map((service) => (
                <label
                  key={service.id}
                  htmlFor={`external-coverage-${service.id}`}
                  className="flex items-center gap-2 text-sm"
                >
                  <Checkbox
                    id={`external-coverage-${service.id}`}
                    checked={selectedIds.includes(service.id)}
                    disabled={save.isPending || service.estimatedDays === null}
                    onCheckedChange={(checked) => {
                      setDraftIds(
                        checked === true
                          ? [...selectedIds, service.id]
                          : selectedIds.filter((id) => id !== service.id)
                      );
                    }}
                  />
                  <span>{service.name}</span>
                  {service.estimatedDays === null && (
                    <span className="text-xs text-muted-foreground">
                      Set delivery days in Services first
                    </span>
                  )}
                </label>
              ))}
              <Button
                type="button"
                disabled={
                  !changed || save.isPending || activeServices.length === 0
                }
                onClick={async () => {
                  try {
                    await save.mutateAsync({
                      areaId: selectedArea.id,
                      serviceCoverages: activeServices.map((service) => ({
                        carrierServiceId: service.id,
                        active: selectedIds.includes(service.id),
                      })),
                    });
                    setDraftIds(null);
                    toast.success("Shipping coverage saved");
                  } catch (error) {
                    toast.error("Could not save coverage", {
                      description:
                        error instanceof Error ? error.message : undefined,
                    });
                  }
                }}
              >
                {save.isPending ? "Saving…" : "Save coverage"}
              </Button>
            </div>
          )}
        </>
      )}
      {legacyMemberships.length > 0 && (
        <div className="space-y-2 rounded-md border border-amber-200 p-3">
          <h4 className="text-sm font-medium">Legacy Carrier Portal access</h4>
          <p className="text-xs text-muted-foreground">
            This integration is now managed in Admin. Disabling a membership
            here leaves the user&apos;s access to other carriers unchanged.
          </p>
          {legacyMemberships.map((membership) => (
            <div
              key={membership.id}
              className="flex items-center justify-between gap-2"
            >
              <span className="text-sm">{membership.name}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disableLegacyAccess.isPending}
                onClick={async () => {
                  try {
                    await disableLegacyAccess.mutateAsync({
                      carrierId,
                      membershipId: membership.id,
                      body: { status: "DISABLED" },
                    });
                    toast.success("Carrier Portal access removed");
                  } catch (error) {
                    toast.error("Could not remove Carrier Portal access", {
                      description:
                        error instanceof Error ? error.message : undefined,
                    });
                  }
                }}
              >
                Remove portal access for {membership.name}
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
